import { useFetchWithAccessToken } from "@/functions/useFetchWithAccessToken";
import { BackendApiUrl } from "@/functions/BackendApiUrl";
import {
  Education,
  Skill,
  UserProfile,
  RegisterResponse,
  AllSkill,
} from "@/services/interfaces/UserProfileInterface";
import { useSwrFetcherWithAccessToken } from "@/functions/useSwrFetcherWithAccessToken";
import useSWR from "swr";
import { ResponseDetails } from "@/functions/tryFetchJson";

// Function to fetch user profile
export function useUserProfile(userId: string | undefined) {
  const swrFetcher = useSwrFetcherWithAccessToken();

  const { data: userProfile, error: userProfileError } = useSWR<UserProfile>(
    userId ? `${BackendApiUrl.getUserProfile}?id=${userId}` : null,
    swrFetcher
  );

  return {
    userProfile,
    userProfileError,
  };
}

// Function to post educations
export function usePostEducations() {
  const { fetchPOST } = useFetchWithAccessToken();

  const postEducations = async (userId: string, educations: Education[]) => {
    const educationsPayload = {
      userId,
      educationDetails: educations,
    };
    const response = await fetchPOST(
      BackendApiUrl.addEducation,
      educationsPayload
    );
    return response;
  };

  return postEducations;
}

// Function to post skills
export function usePostSkills() {
  const { fetchPOST } = useFetchWithAccessToken();

  const postSkills = async (userId: string, skills: Skill[]) => {
    const skillNamesPayload = {
      userId,
      skillNames: skills.map((s) => s.skillName),
    };
    const response = await fetchPOST(BackendApiUrl.addSkill, skillNamesPayload);
    return response;
  };

  return postSkills;
}

export function useGetAllSkill() {
  const { fetchPOST } = useFetchWithAccessToken();

  const postGetSkills = async (
    skillName: string,
    skillExists: string[]
  ): Promise<AllSkill[]> => {
    const getSkillRequest = {
      skillName,
      skillExists,
    };
    const response = await fetchPOST(
      BackendApiUrl.getAllSkill,
      getSkillRequest
    );
    return response.data as AllSkill[];
  };

  return postGetSkills;
}

// Function to handle user registration
export function useRegisterUserProfile() {
  const { fetchPOSTWithFormData } = useFetchWithAccessToken();

  const register = async (
    userId: string,
    encryptedData: {
      encryptedEmail: string;
      encryptedFullName: string;
      encryptedDateOfBirth: string;
      encryptedPhoneNumber: string;
      encryptedPassword: string;
    },
    domicile: string,
    yearsOfExperience: string,
    profileImageFile: File | null,
    resumeFile: File | null
  ) => {
    if (profileImageFile == null || resumeFile == null) return;

    const formData = new FormData();
    formData.append("id", userId);
    formData.append("FullName", encryptedData.encryptedFullName);
    formData.append("DateOfBirth", encryptedData.encryptedDateOfBirth);
    formData.append("Domicile", domicile);
    formData.append("Email", encryptedData.encryptedEmail);
    formData.append("PhoneNumber", encryptedData.encryptedPhoneNumber);
    formData.append("YearsOfExperience", yearsOfExperience.toString());
    formData.append("ProfileImage", profileImageFile);
    formData.append("Resume", resumeFile);
    formData.append("Password", encryptedData.encryptedPassword);

    const response: ResponseDetails<RegisterResponse> =
      await fetchPOSTWithFormData(BackendApiUrl.register, formData);
    return response;
  };

  return {
    register,
  };
}

// Function to handle editing user profile
export function useEditUserProfile() {
  const { fetchPUTWithFormData } = useFetchWithAccessToken();

  const editProfile = async (
    userId: string,
    encryptedData: {
      encryptedEmail: string;
      encryptedFullName: string;
      encryptedDateOfBirth: string;
      encryptedPhoneNumber: string;
    },
    domicile: string,
    yearsOfExperience: string,
    profileImageFile: File | null,
    resumeFile: File | null
  ) => {
    const formData = new FormData();
    formData.append("id", userId);
    formData.append("FullName", encryptedData.encryptedFullName);
    formData.append("DateOfBirth", encryptedData.encryptedDateOfBirth);
    formData.append("Domicile", domicile);
    formData.append("Email", encryptedData.encryptedEmail);
    formData.append("PhoneNumber", encryptedData.encryptedPhoneNumber);
    formData.append("YearsOfExperience", yearsOfExperience.toString());

    if (profileImageFile) {
      formData.append("NewProfilePic", profileImageFile);
    }

    if (resumeFile) {
      formData.append("NewResume", resumeFile);
    }

    const response = await fetchPUTWithFormData(
      BackendApiUrl.editPersonalData,
      formData
    );
    return response;
  };

  return {
    editProfile,
  };
}
// Function to handle deleting an education record
export function useDeleteEducation() {
  const { fetchDELETE } = useFetchWithAccessToken();

  const deleteEducation = async (educationId: string) => {
    const response = await fetchDELETE(
      BackendApiUrl.deleteEducation + `?Id=${educationId}`
    );
    return response;
  };

  return {
    deleteEducation,
  };
}
